import { ResultStatus } from '@carvana/merch-result';
import React, { useContext } from 'react';
import { cvnaCategoryNamesSortOrder } from '@/vdp/KeyFeatures/constants';
import { type OEMDataItem } from '@/vdp/Modals/FeaturesAndSpecsModal/OEMPanel';
import AvailabilityAction from '~/constants/availabilityAction';
import AvailabilityError from '~/constants/availabilityError';
import HighlightKey from '~/constants/common/highlightKey';
import TagIds from '~/constants/common/tagIds';
import CtaType from '~/constants/ctaType';
import { blobCdnUrl } from '~/constants/environment';
import gallerySafetyFeatures from '~/constants/gallerySafetyFeatures';
import { InventoryTypeKey } from '~/constants/inventoryType';
import OrderState from '~/constants/orderState';
import PhotoInventoryType from '~/constants/PhotoinventoryType';
import PurchaseType from '~/constants/purchaseType';
import Sections from '~/constants/sections';
import Store from '~/constants/store';
import TransitChargeState from '~/constants/TransitChargeState';
import CvnaTier from '~/constants/vdp/cvnaTier';
import { VehicleLockTypeKey } from '~/constants/vehicleLockType';
import { ContextType as DiscountContext } from '~/context/common/DiscountContext';
import { ContextType as TradeContext } from '~/context/common/TradeContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import type { DeliveryPickup } from '~/contracts/common';
import type { Availability, CvnaCategory, ReduxStore } from '~/contracts/vdp';
import createSelector from '~/store/createSelector';
import { transformExteriorImages } from '~/transform/imagery';

// Tracking

export const getAnalytics = createSelector(
  (state: ReduxStore) => state.vehicle.analytics,
  v => v
);

export const getAssociatedVdpEventId = createSelector(
  getAnalytics,
  ({ associatedVdpEventId }) => associatedVdpEventId
);
export const getSearchRequestId = createSelector(
  getAnalytics,
  ({ correspondingSearchRequestId }) => correspondingSearchRequestId
);

// General

export const getExperimentLoaded = createSelector(
  (state: ReduxStore) => state.experimentation.isClientLoaded,
  (state: ReduxStore) => state.experimentation.isServerLoaded,
  (isClientLoaded, isServerLoaded) => isClientLoaded && isServerLoaded
);

export const getExperiments = createSelector(
  (state: ReduxStore) => state.experimentation.experiments,
  experiments => experiments
);

export const getIsLite = createSelector(
  (state: ReduxStore) => state.ui.isLite,
  isLite => isLite
);

export const getActivationModalStatus = createSelector(
  (state: ReduxStore) => state.router.queryParams,
  queryParams => {
    const params = Object.keys(queryParams).map(v => v.toLowerCase());
    const isActivationModalQueryPresent =
      params.includes('oneusetoken') &&
      !params.includes('isfinsearch') &&
      queryParams.actmodal !== 'finance';

    return {
      isOpen: isActivationModalQueryPresent
    };
  }
);

export const getFinanceActivationModalStatus = createSelector(
  (state: ReduxStore) => state.router.queryParams,
  queryParams => {
    const params = Object.keys(queryParams).map(v => v.toLowerCase());
    const isActivationModalQueryPresent =
      params.includes('oneusetoken') &&
      (params.includes('isfinsearch') || queryParams.actmodal === 'finance');

    return {
      isOpen: isActivationModalQueryPresent
    };
  }
);

export const getSearchQueryParams = createSelector(
  (state: ReduxStore) => state.router.queryParams,
  search => search
);

export const getVehicle = createSelector(
  (state: ReduxStore) => state.vehicle,
  vehicle => vehicle
);
export const getVehicleDetails = createSelector(
  (state: ReduxStore) => state.vehicle.details,
  vehicleDetails => vehicleDetails
);

export const getShowEVTaxTreatment = createSelector(
  getVehicleDetails,
  vehicleDetails => Boolean(vehicleDetails.evTaxCreditAmount)
);

export const getVehicleId = createSelector(
  getVehicleDetails,
  ({ vehicleId }) => vehicleId ?? 0
);

export const getVehicleStockNumber = createSelector(
  getVehicleDetails,
  basics => basics.stockNumber ?? 0
);

export const getVehicleIdAndStockNumber = createSelector(
  getVehicleId,
  getVehicleStockNumber,
  (vehicleId, stockNumber) => ({ stockNumber, vehicleId })
);

export const getInventoryType = createSelector(
  getVehicleDetails,
  ({ inventoryType }) => inventoryType ?? InventoryTypeKey.Core
);

const getImperfections = createSelector(
  getVehicleDetails,
  ({ vexVdpImageData }) => vexVdpImageData?.imperfections ?? []
);

export const getInteriorImperfections = createSelector(
  getImperfections,
  imperfections =>
    imperfections.filter(
      imperfection => imperfection.location?.toLowerCase().includes('interior')
    )
);

export const getExteriorImperfections = createSelector(
  getImperfections,
  imperfections =>
    imperfections.filter(
      imperfection => imperfection.location?.toLowerCase().includes('exterior')
    )
);

// Spinner

export const getSpinnerType = createSelector(
  getVehicle,
  ({ spinner }) => spinner.type
);

export const getSpinnerIsImmersive = createSelector(
  getVehicle,
  ({ spinner }) => spinner.isImmersive
);

export const getVehicleInfoForSpinner = createSelector(
  getVehicleDetails,
  ({ installedPackages = [], make }) => ({ installedPackages, make })
);

/**
 * Determines if a vehicle is a WIP or not. Very rarely do you need this
 * information. Only in cases of imagery should the be used. In most cases you
 * should use `getIsReservableVehicle` to determine if a vehicle is reservable
 * or not.
 */
export const getIsWIPVehicle = createSelector(
  getInventoryType,
  inventoryType => inventoryType === InventoryTypeKey.WIP
);

export const getActivationModalVehicleData = createSelector(
  getVehicleStockNumber,
  getVehicleDetails,
  getIsWIPVehicle,
  (
    stockNumber,
    {
      make = '',
      mileage = 0,
      model = '',
      price = 0,
      trim = '',
      vehicleId = 0,
      stockImageData = {},
      year = 0
    },
    isWIPVehicle
  ) => ({
    imageUrl: isWIPVehicle
      ? stockImageData?.jellyBeanDesktopUrl ?? ''
      : `/${stockNumber}/post-large/${stockNumber}-edc-02-search.jpg`,
    isOnDemand: isWIPVehicle,
    make,
    mileage,
    model,
    price: { total: price },
    stockNumber,
    trim,
    vehicleId,
    vehicleLockType: 0,
    year
  })
);

export const getVehicleAvailableDateTime = createSelector(
  (state: ReduxStore) => state?.user?.availability?.vehicleAvailableDateTime,
  vehicleAvailableDateTime =>
    vehicleAvailableDateTime ? Date.parse(vehicleAvailableDateTime) : null
);

export const getIsRecentlyAdded = createSelector(
  getVehicleDetails,
  ({ highlights }) =>
    highlights?.some(({ tagId }) => tagId === TagIds.RecentlyAdded) || false
);

export const getUser = createSelector(
  (state: ReduxStore) => state.user,
  user => user
);

export const getUserAvailability = createSelector(
  getUser,
  user => user.availability ?? ({} as Partial<Availability>)
);

export const getIsReservableVehicle = createSelector(
  getVehicleDetails,
  getUserAvailability,
  ({ purchaseType }, { error }) =>
    purchaseType === PurchaseType.Reservable ||
    error === AvailabilityError.UnavailableReserved
);

export const getDeliveryPickup = createSelector(
  getVehicle,
  ({ deliveryPickup }) => deliveryPickup
);

export const getPickupInfo = createSelector(
  getDeliveryPickup,
  deliveryPickup => {
    const pickup = deliveryPickup.data.soonestPickup;

    return pickup.status === ResultStatus.Succeeded
      ? pickup.content
      : undefined;
  }
);

export const getDeliveryInfo = createSelector(
  getDeliveryPickup,
  deliveryPickup => {
    const delivery = deliveryPickup.data.soonestDelivery;

    return delivery.status === ResultStatus.Succeeded
      ? delivery.content
      : undefined;
  }
);

export const getSoonestRoute = createSelector(
  getDeliveryInfo,
  getPickupInfo,
  (delivery, pickup): DeliveryPickup | undefined => {
    if (pickup == null) return delivery;

    if (delivery == null) return pickup;

    if (pickup.drivingMiles > 150) return delivery;

    return delivery.daysToAvailability < pickup.daysToAvailability
      ? delivery
      : pickup;
  }
);

export const getTransitChargeInfo = createSelector(
  getVehicle,
  ({ transitCharge }) => {
    const state = (() => {
      if (transitCharge.hasError || !transitCharge.isLoaded) {
        return TransitChargeState.Unknown;
      }

      return transitCharge.data > 0
        ? TransitChargeState.HasCharge
        : TransitChargeState.NoCharge;
    })();

    return {
      ...transitCharge,
      state
    };
  }
);

export const isCurrentVehicleFavorited = createSelector(
  getVehicle,
  vehicle => vehicle.isFavorited
);

export const isSubscribedToNotifications = createSelector(
  getVehicle,
  vehicle => vehicle.isSubscribedToNotifications
);

export const getSoftPullStatus = createSelector(
  getUser,
  ({ softPullStatus }) => softPullStatus
);

export const getHasCorePurchaseAndIsViewingReservable = createSelector(
  getUser,
  getIsReservableVehicle,
  ({ purchasing }, isReservable) =>
    Boolean(purchasing) &&
    purchasing?.orderState !== OrderState.Reserved &&
    isReservable
);

export const getIsVehicleReservedByUser = createSelector(
  getUserAvailability,
  getIsReservableVehicle,
  ({ isLockedByThisUser = false }, isReservable) =>
    isLockedByThisUser && isReservable
);

export const getIsVehicleReservedByOther = createSelector(
  getUserAvailability,
  getIsReservableVehicle,
  ({ isLockedByThisUser, error }, isReservable) =>
    !isLockedByThisUser &&
    error === AvailabilityError.PurchasePending &&
    isReservable
);

export const getIsInExclusivePreview = createSelector(
  getUserAvailability,
  ({ error }) => error === AvailabilityError.OnSitePrivate
);

export const getShowAvailabilityLoader = createSelector(
  getUserAvailability,
  ({ isLoaded }) => !isLoaded
);

export const getIsAvailabilityDisabled = createSelector(
  getUserAvailability,
  ({ action, isLoaded }) =>
    isLoaded &&
    (action === AvailabilityAction.GetStartedDisabled ||
      action === AvailabilityAction.PreOrderNowDisabled)
);

// Universal CTA
export const getCtaButtonState = createSelector(
  getUserAvailability,
  getVehicleStockNumber,
  getVehicleId,
  getShowAvailabilityLoader,
  getIsAvailabilityDisabled,
  (
    { action },
    stockNumber,
    vehicleId,
    showAvailabilityLoader,
    isAvailabilityDisabled
  ) => {
    const isDisabled = showAvailabilityLoader || isAvailabilityDisabled;
    const getStarted = action === AvailabilityAction.GetStarted;
    const preOrderNow =
      action === AvailabilityAction.PreOrderNow ||
      action === AvailabilityAction.PreOrderNowDisabled;
    const continuePurchase = action === AvailabilityAction.ContinuePurchase;
    const continueReservation =
      action === AvailabilityAction.ContinueReservation;
    const notifyMe = action === AvailabilityAction.NotifyMe;

    let type: CtaType;

    switch (true) {
      case continuePurchase:
        type = CtaType.ContinuePurchase;
        break;
      case getStarted:
        type = CtaType.GetStarted;
        break;
      case continueReservation:
        type = CtaType.Reservation;
        break;
      case notifyMe:
        type = CtaType.NotifyMe;
        break;
      case preOrderNow:
        type = CtaType.Reservable;
        break;
      case showAvailabilityLoader:
        type = CtaType.Loader;
        break;
      default:
        type = CtaType.Default;
    }

    return {
      continuePurchase,
      getStarted,
      isDisabled,
      notifyMe,
      showGetStartedHelp: getStarted || continuePurchase,
      stockNumber,
      type,
      vehicleId,
      wip: preOrderNow
    };
  }
);

export const getIsNotAvailableForImmediatePurchase = createSelector(
  getCtaButtonState,
  getIsReservableVehicle,
  getIsAvailabilityDisabled,
  ({ notifyMe }, isReservable, isAvailabilityDisabled) =>
    Boolean(isAvailabilityDisabled || isReservable || notifyMe)
);

export const getCanShowDeliveryAndPickupInfo = createSelector(
  getIsNotAvailableForImmediatePurchase,
  getUserAvailability,
  (isNotPurchasable, { isLoaded: availabilityLoaded }) =>
    !isNotPurchasable && availabilityLoaded
);

// must return false in default loading state to ensure no layout shift
export const getShouldHideDeliveryPickup = createSelector(
  getIsNotAvailableForImmediatePurchase,
  getDeliveryPickup,
  getDeliveryInfo,
  getPickupInfo,
  (isNotPurchasable, { hasError, isLoaded }, deliveryInfo, pickupInfo) =>
    isNotPurchasable || hasError || (isLoaded && !deliveryInfo && !pickupInfo)
);

// Vehicle Details Section

export const getIsLightAccidentVehicle = createSelector(
  getVehicleDetails,
  ({ stores = [] }) => stores.includes(Store.LightAccident)
);

export const getSpinnerData = createSelector(
  getVehicleDetails,
  ({ vexVdpImageData }) => vexVdpImageData || {}
);

export const getHasImages = createSelector(
  getSpinnerData,
  ({ features = [], imperfections = [], spins = [] }) =>
    Boolean(
      features.length ||
        imperfections.length ||
        transformExteriorImages(spins).length
    )
);

export const getKBBPromoAmount = createSelector(
  getVehicleDetails,
  ({ price = 0, kbbValue = 0, highlights }) => {
    const hasGreatDealHighlight = highlights?.some(
      ({ tagKey }) => tagKey === HighlightKey.GreatDeal
    );
    const kbbValueIsGreaterThan0 = kbbValue > 0;
    const kbbValuePriceDifference = kbbValue - price;
    const priceDifferenceAtLeast1500 = kbbValuePriceDifference >= 1500;
    const shouldShowPromo =
      price > 0 &&
      kbbValueIsGreaterThan0 &&
      priceDifferenceAtLeast1500 &&
      hasGreatDealHighlight;

    return shouldShowPromo ? kbbValuePriceDifference : 0;
  }
);

export const getHasOwnerReviews = createSelector(
  getVehicle,
  ({ hasOwnerReviews }) => hasOwnerReviews
);

export const getVehicleDetailsForNative = createSelector(
  getVehicleDetails,
  getIsReservableVehicle,
  getUserAvailability,
  (
    vehicle,
    isReservable,
    availability
  ): {
    ctaAction: AvailabilityAction;
    inventoryType: InventoryTypeKey;
    isLocked: boolean;
    locationId: number;
    lockType: VehicleLockTypeKey;
    make: string;
    mileage: number;
    model: string;
    price: number;
    purchaseType: PurchaseType;
    saleStatus: string;
    stockNumber: number;
    trim: string;
    vehicleId: number;
    vehicleImage: string;
    year: number;
  } => {
    const {
      stockImageData: { jellyBeanMobileUrl = '' } = {},
      stockNumber = 0,
      vexVdpImageData: { heroes: { hero = '' } = {} } = {}
    } = vehicle;

    return {
      ctaAction: availability.action,
      inventoryType: vehicle.inventoryType ?? InventoryTypeKey.Core,
      isLocked: availability.lockType !== VehicleLockTypeKey.None,
      locationId: vehicle.location?.locationId ?? 0,
      lockType: availability.lockType,
      make: vehicle.make ?? '',
      mileage: vehicle.mileage ?? 0,
      model: vehicle.model ?? '',
      price: vehicle.price ?? 0,
      purchaseType: vehicle.purchaseType ?? PurchaseType.Purchasable,
      saleStatus: vehicle.saleStatus ?? '',
      stockNumber,
      trim: vehicle.trim ?? '',
      vehicleId: vehicle.vehicleId ?? 0,
      vehicleImage: isReservable
        ? jellyBeanMobileUrl
        : hero ||
          `${blobCdnUrl}/${stockNumber}/pre/original/${stockNumber}-edc-03.jpg`,
      year: vehicle.year ?? 0
    };
  }
);

// Alert Modals
export const getAlertStatus = createSelector(
  getUserAvailability,
  getIsReservableVehicle,
  ({ error: alertToShow, isError }, isReservable) => {
    const purchasePending = alertToShow === AvailabilityError.PurchasePending;
    const unavailable = alertToShow === AvailabilityError.Unavailable;
    const stopSaleRecall = alertToShow === AvailabilityError.StopSaleRecall;
    const outOfMarket = alertToShow === AvailabilityError.OutOfMarket;
    const isSold = alertToShow === AvailabilityError.IsSold;
    const notAvailableInCA = alertToShow === AvailabilityError.NotAvailableInCA;
    const isReservableAndPurchasePending = isReservable && purchasePending;

    const showAlert =
      !isError &&
      alertToShow !== AvailabilityError.None &&
      alertToShow !== AvailabilityError.OnSitePrivate &&
      alertToShow !== AvailabilityError.UnavailableReserved &&
      !isReservableAndPurchasePending &&
      !purchasePending;

    return {
      purchasePending,
      unavailable,
      stopSaleRecall,
      outOfMarket,
      isSold,
      showAlert,
      notAvailableInCA
    };
  }
);

export const getRecalls = createSelector(
  getVehicle,
  ({ alertModals }) => alertModals.recallStatus
);

export const getIsDealerMarketplace = createSelector(
  getVehicleDetails,
  ({ inventoryType, stores }) =>
    inventoryType === InventoryTypeKey.DealerMarketplace ||
    (stores?.includes(Store.DMP2Core) ?? false)
);

export const getIs3PR = createSelector(getVehicleDetails, ({ stores = [] }) =>
  stores.includes(Store.Manheim)
);

export const getIsPartnerRetailVehicle = createSelector(
  getVehicleDetails,
  ({ stores = [] }) => stores.includes(Store.PartnerRetail)
);

export const getIsPartnerVehicle = createSelector(
  getIsDealerMarketplace,
  getIs3PR,
  (isMarketplace, is3PR) => isMarketplace || is3PR
);

const getPhotoInventoryType = createSelector(
  getVehicleDetails,
  ({ vexVdpImageData }) => vexVdpImageData?.captureInfo?.inventoryType
);

export const getIsMobileCapture = createSelector(
  getIsPartnerVehicle,
  getPhotoInventoryType,
  (isPartnerVehicle, photoInventoryType) =>
    isPartnerVehicle ||
    (photoInventoryType && photoInventoryType !== PhotoInventoryType.Core)
);

export const getLastModified = createSelector(
  getVehicleDetails,
  ({ vexVdpImageData }) => vexVdpImageData?.captureInfo?.lastModified ?? ''
);

export const getSafetyFeatures = createSelector(
  getVehicleDetails,
  ({ generalizedFacets = [] }) =>
    generalizedFacets.filter(facet =>
      gallerySafetyFeatures.includes(facet.facetName)
    )
);

const getIsLazyLoadDisabled = createSelector(
  (state: ReduxStore) => state.ui.isLazyLoadEnabled,
  isLazyLoadEnabled => !isLazyLoadEnabled
);

export const getIsSpinnerDisabled = createSelector(
  getIsWIPVehicle,
  (state: ReduxStore) => state.ui.isSpinnerDisabled,
  (isWIPVehicle, isDisabled) => isWIPVehicle || isDisabled
);

export const getSectionMounted = createSelector(
  (state: ReduxStore) => state.ui.sectionMounted,
  sectionMounted => sectionMounted
);

// phase 1 of lazy loading
export const getShouldRenderBelowSpinner = createSelector(
  getIsWIPVehicle,
  getIsLazyLoadDisabled,
  getVehicle,
  getIsSpinnerDisabled,
  (isWIPVehicle, isLazyLoadDisabled, { spinner }, isSpinnerDisabled) =>
    isLazyLoadDisabled ||
    isWIPVehicle ||
    spinner.hasError ||
    spinner.isScriptInserted ||
    isSpinnerDisabled
);

// phase 2 of lazy loading
export const getShouldLoadFirstSection = createSelector(
  getShouldRenderBelowSpinner,
  getIsLazyLoadDisabled,
  getIsWIPVehicle,
  (state: ReduxStore) => state.ui.sectionViewed[Sections.Gallery],
  (shouldRenderBelowSpinner, isLazyLoadDisabled, isWIP, viewedGallery) =>
    shouldRenderBelowSpinner && (isLazyLoadDisabled || viewedGallery || isWIP)
);

// final phase of lazy loading
export const getShouldLoadBelowFirstSectionStandard = createSelector(
  getShouldRenderBelowSpinner,
  getIsLazyLoadDisabled,
  (state: ReduxStore) => state.ui.sectionViewed[Sections.VehicleDetails],
  (shouldRenderBelowSpinner, isLazyLoadDisabled, viewedFirstSection) =>
    shouldRenderBelowSpinner && (isLazyLoadDisabled || viewedFirstSection)
);

export const getShouldShowPriceDetails = createSelector(
  getAlertStatus,
  getVehicleDetails,
  ({ outOfMarket }, { price = 0 }) => !outOfMarket && price > 0
);

export function useIsPriceLoading(
  discountContext: React.Context<DiscountContext>,
  tradeContext: React.Context<TradeContext>
) {
  const { discounts } = useContext(discountContext);
  const { trades } = useContext(tradeContext);
  const isLoggedIn = useIsLoggedIn();

  return !discounts.isLoaded || (isLoggedIn && !trades.isLoaded);
}

const equivalentFeatures: Record<string, string> = {
  'Heated Seats': 'Heated and Ventilated Seats',
  'Tow Hitch': 'Tow Hitch and Connector',
  'Rear View Camera': '360 Camera',
  'Front View Camera': '360 Camera',
  'Side View Cameras': '360 Camera',
  'Tilt Wheel': 'Power Adjustable Steering Column',
  'Traction Control': 'Intelligent Traction Control',
  'Stability Control': 'Electronic Stability Control',
  'Integrated Garage Door Opener': 'HomeLink'
};

export const getFilteredCvnaCategories = createSelector(
  getVehicleDetails,
  ({ cvnaCategories = [] }) => {
    const possiblePreferredFeatures = new Set(
      Object.values(equivalentFeatures)
    );
    const actualPreferredFeatures = cvnaCategories.reduce<string[]>(
      (acc, category) =>
        acc.concat(
          category.cvnaFeatures
            .filter(feature => possiblePreferredFeatures.has(feature.name))
            .map(feature => feature.name)
        ),
      []
    );

    return cvnaCategories.map<CvnaCategory>(category => ({
      ...category,
      cvnaFeatures: category.cvnaFeatures.filter(
        feature =>
          !actualPreferredFeatures.includes(equivalentFeatures[feature.name])
      )
    }));
  }
);

export const getOrderedCvnaCatagories = createSelector(
  getFilteredCvnaCategories,
  cvnaCategories =>
    cvnaCategories.sort((a, b) => {
      const orderOfA = cvnaCategoryNamesSortOrder.indexOf(a.name);
      const orderOfB = cvnaCategoryNamesSortOrder.indexOf(b.name);

      return orderOfA - orderOfB;
    })
);

export const getStandoutTierCategories = createSelector(
  getOrderedCvnaCatagories,
  categories =>
    categories.filter(({ cvnaFeatures }) =>
      cvnaFeatures.some(({ tier }) => tier === CvnaTier.standout)
    )
);

export const getOEMData = createSelector(
  getVehicleDetails,
  ({
    installedPackages = [],
    installedOptions = [],
    standardEquipment = []
  }) => {
    const hasInstalledPackages = installedPackages.length > 0;
    const hasInstalledOptions = installedOptions.length > 0;
    const hasStandardEquipment = standardEquipment.length > 0;
    const showOEMData =
      hasInstalledPackages || hasInstalledOptions || hasStandardEquipment;

    const maybeOEMData: Array<OEMDataItem | null> = [
      hasInstalledPackages
        ? {
            title: 'Installed Packages',
            data: installedPackages.map(pack => ({
              name: pack.name,
              options: pack.options
            }))
          }
        : null,
      hasInstalledOptions
        ? {
            title: 'Installed Options',
            data: [
              { name: '', options: installedOptions.map(option => option.name) }
            ]
          }
        : null,
      hasStandardEquipment
        ? {
            title: 'Standard Equipment',
            data: [{ name: '', options: [...standardEquipment] }]
          }
        : null
    ];

    return {
      showOEMData,
      oemData: maybeOEMData.filter((data): data is OEMDataItem => Boolean(data))
    };
  }
);

export const getPriorUseTypes = createSelector(
  getVehicleDetails,
  ({ autocheckReport }) =>
    autocheckReport?.ownerUseTypes?.map(({ useType }) => useType)
);
